/**
 * A site that lives in the browser rather than in our database — the example, and the site the model
 * wrote for somebody with no account.
 *
 * ⚠️ NOTHING HERE TOUCHES THE API, AND THAT IS THE POINT. Pressing the button on one of the landing
 * page's examples creates no account, no site record and no address: it opens a document that already
 * exists (`@ksa/model`'s `SAMPLES`) in the editor, in this tab. Typing your own description spends one
 * model call and comes back with a document and no id (CLAUDE.md §5.14) — which lands here too. A
 * visitor gets to move a section, rewrite a heading and switch the colours before anybody asks them
 * who they are.
 *
 * ## ⚠️ TWO STORES, BECAUSE A DEMONSTRATION AND A DRAFT ARE NOT THE SAME THING
 *
 * > ⚠️ **THIS FILE USED `sessionStorage` FOR BOTH, AND THE REASONING IS KEPT BECAUSE HALF OF IT IS
 * > STILL RIGHT.** It said: `localStorage` "would greet them a fortnight later with a half-edited
 * > flower shop they have forgotten, on a machine that may not be theirs — and this is a
 * > demonstration, not a draft." True of the flower shop. Not true of the site somebody described
 * > their own business to get, which IS a draft, and which the same sentence was quietly deciding
 * > for.
 *
 * - **A sample is `sessionStorage`.** It is one of five documents that are always available from the
 *   front page; losing it costs a click, and keeping it for a fortnight is clutter on somebody's
 *   machine with nothing in it that was theirs.
 * - **A draft is `localStorage`, and it expires after thirty days.** It cost a model call and it was
 *   written from a sentence about somebody's own business. `sessionStorage` dies with the tab, so
 *   closing it by accident — or following a link out and pressing back too late — threw away the one
 *   thing the product had made for them, silently, with no way to get the same site again.
 *
 * ⚠️ **THE THIRTY DAYS IS THE SWEEP, AND THE BROWSER IS WHAT RUNS IT.** An unclaimed site has to be
 * cleaned up eventually or it is litter for ever. Doing that on the server would mean STORING a
 * stranger's site first — an anonymous write path, a bearer-capability URL, a pile of records nobody
 * owns and a scheduled job to delete them — which is precisely what §5.14 took out. Storing it in the
 * browser puts the site where its only reader is, and makes the sweep a comparison against a
 * timestamp on the way past. It is exact, it costs nothing, and there is nothing to break into.
 *
 * ⚠️ AND `DRAFT_TTL_MS` IS THIS NUMBER WRITTEN A THIRD TIME. `apps/api/src/routes/sites.ts` sweeps a
 * signed-in customer's untouched drafts at thirty days and `Sites.tsx` tells them so on the card;
 * this is the same promise made to somebody who has not signed in. They are three numbers because
 * they are three unrelated mechanisms — a Firestore query, a sentence, and a browser — and what
 * disagreement costs is a product that says thirty days in one place and means something else in
 * another.
 *
 * ⚠️ IT IS KEYED BY DOCUMENT, so opening the coffee roastery does not overwrite the edits somebody
 * left on the dental clinic. `changed` is stored beside the document because it is the thing the
 * on-screen warning is about: an untouched example has nothing to lose, and saying "your changes are
 * not saved" to somebody who has made none is the sort of banner people learn to stop reading
 * (CLAUDE.md §0.4).
 */

import type { Site } from '@ksa/model'

/**
 * Which of the two this is — and therefore which store it lives in and whether it expires.
 *
 * ⚠️ IT IS PASSED RATHER THAN INFERRED FROM THE ID. A draft id happens to begin with `draft-`, and
 * building the store choice on that would make the prefix load-bearing in a second file that never
 * says so. The caller always knows: the route it is serving is the answer.
 */
export type LocalKind = 'sample' | 'draft'

export type DemoState = {
  document: Site
  /** Whether anything has been edited. Until it has, there is nothing to keep and nothing to warn about. */
  changed: boolean
  /**
   * Whether the arrival dialog has already been shown for this document.
   *
   * ⚠️ IT IS REMEMBERED BESIDE THE DOCUMENT RATHER THAN FOR THE TAB, so opening the flower shop and
   * then a site of your own greets you once for each — they are two different things to be told
   * about. It also means a reload, which this store exists to survive, does not re-open a dialog
   * somebody has already read and closed.
   */
  greeted?: boolean
  /**
   * When it was last written, so a draft can expire.
   *
   * ⚠️ WRITTEN ON EVERY SAVE, SO THE THIRTY DAYS IS SINCE IT WAS LAST TOUCHED rather than since it
   * was built. Somebody still working on a site in week five is working on it, and a sweep that
   * counted from creation would delete it out from under them.
   */
  savedAt?: number
}

const PREFIX = 'ksa:demo:'

/** Thirty days without a touch. See the note above on why this number exists three times. */
export const LOCAL_TTL_MS = 30 * 24 * 60 * 60 * 1000

/**
 * A key for a site the model just wrote for somebody with no account.
 *
 * ⚠️ ONE PER GENERATION RATHER THAN ONE SLOT CALLED `draft`. Two tabs building two businesses is an
 * ordinary thing to do — somebody comparing what the model makes of two descriptions — and a single
 * slot would have the second quietly overwrite the first. It is also why the id is in the URL: the
 * tab's address is what says which document this is.
 *
 * ⚠️ AND IT IS NOT A SITE ID. Nothing has been created; this string never leaves the browser, and the
 * server has never seen it.
 */
export function newDraftId(): string {
  return `draft-${Math.random().toString(36).slice(2, 10)}`
}

/**
 * ⚠️ A REFUSED OR MISSING STORE IS "NOTHING SAVED", NEVER A CRASH. Some privacy modes refuse storage
 * outright, and a demonstration that throws on a browser that refuses it is a blank screen on the one
 * page the product is being judged on. The example simply opens fresh.
 */
function storeFor(kind: LocalKind): Storage | null {
  try {
    return kind === 'draft' ? window.localStorage : window.sessionStorage
  } catch {
    return null
  }
}

/**
 * ⚠️ WHERE THE DOCUMENT WAITS OUT A SIGN-IN. The claim path leaves the tab for an identity provider
 * and comes back to `/demo/<id>?keep=1`; the document has to survive that, and it does, because both
 * stores outlive a navigation away and back.
 */
export function readDemo(id: string, kind: LocalKind): DemoState | null {
  const store = storeFor(kind)
  if (!store) return null

  // ⚠️ ON THE WAY PAST, NOT ON A TIMER. There is no process here to schedule anything on, and a
  // browser that is not open is not sweeping — so the sweep happens when somebody opens a draft,
  // which is the only moment any of this is observable.
  if (kind === 'draft') sweepExpired(store)

  try {
    const raw = store.getItem(PREFIX + id)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<DemoState>
    if (!parsed || typeof parsed !== 'object' || !parsed.document) return null
    if (kind === 'draft' && expired(parsed.savedAt)) {
      store.removeItem(PREFIX + id)
      return null
    }
    /*
     * ⚠️ EVERY FIELD IS NAMED HERE, AND THAT IS A TRAP AS WELL AS A GUARD. Rebuilding the object
     * rather than returning what was parsed is what keeps a hand-edited stored value from becoming
     * application state — but it also means a field added to `DemoState` and not added HERE is
     * silently dropped on every read. `greeted` was, and the symptom was the arrival dialog
     * reappearing on every reload having been dismissed: written correctly, read away again.
     */
    return {
      document: parsed.document as Site,
      changed: parsed.changed === true,
      greeted: parsed.greeted === true,
      savedAt: typeof parsed.savedAt === 'number' ? parsed.savedAt : undefined,
    }
  } catch {
    return null
  }
}

export function writeDemo(id: string, kind: LocalKind, state: DemoState): void {
  const store = storeFor(kind)
  if (!store) return
  try {
    store.setItem(PREFIX + id, JSON.stringify({ ...state, savedAt: Date.now() }))
  } catch {
    /*
     * ⚠️ A FULL STORE IS NOT AN ERROR TO SHOW ANYBODY, and on `localStorage` it is a real state: a
     * few sites of a few dozen kilobytes each against a five-megabyte quota is fine, and somebody
     * whose browser is already full is not going to be helped by hearing about it from us. The edits
     * live in memory for as long as the tab does, which is what somebody with storage switched off
     * already expects from every site they visit.
     */
  }
}

export function clearDemo(id: string, kind: LocalKind): void {
  try {
    storeFor(kind)?.removeItem(PREFIX + id)
  } catch {
    /* nothing to clear */
  }
}

function expired(savedAt: number | undefined): boolean {
  // ⚠️ A DRAFT WITH NO TIMESTAMP IS KEPT, NOT SWEPT. The only ones without are those written by a
  // build from before this field existed, and deleting somebody's work because of our own deploy is
  // the one outcome this whole file is arranged to avoid. It gets a timestamp on its next save.
  if (typeof savedAt !== 'number') return false
  return Date.now() - savedAt > LOCAL_TTL_MS
}

/**
 * ⚠️ IT SWEEPS EVERY EXPIRED DRAFT, NOT ONLY THE ONE BEING ASKED FOR. Somebody who built three sites
 * in March and comes back in May opens one of them; the other two are just as expired and just as
 * much theirs to have cleaned up. Reading the whole store is a handful of keys — this is one origin's
 * `localStorage`, not a database.
 */
function sweepExpired(store: Storage): void {
  try {
    const stale: string[] = []
    for (let i = 0; i < store.length; i++) {
      const key = store.key(i)
      if (!key || !key.startsWith(PREFIX)) continue
      const raw = store.getItem(key)
      if (!raw) continue
      const savedAt = (JSON.parse(raw) as Partial<DemoState>)?.savedAt
      if (expired(savedAt)) stale.push(key)
    }
    for (const key of stale) store.removeItem(key)
  } catch {
    /* a store that will not enumerate is a store with nothing to sweep */
  }
}
